import { BaseCommandHandler, CommandResult, PropertyChange } from '../BaseCommandHandler';
import { Component, ComponentType } from '@/types';

const TABLE_THEMES: Record<string, Record<string, unknown>> = {
  // Original 7 themes (unchanged)
  'professional': {
    headerStyle: 'background', headerBackgroundColor: '#1F2937',
    headerTextColor: '#ffffff', striped: true, showBorders: true, hoverable: true,
  },
  'minimal': {
    headerStyle: 'typography', showBorders: false, striped: false,
    hoverable: true, rowPadding: 'compact',
  },
  'striped': {
    headerStyle: 'background', headerBackgroundColor: '#f3f4f6',
    headerTextColor: '#111827', striped: true, showBorders: false, hoverable: true,
  },
  'bordered': {
    headerStyle: 'background', headerBackgroundColor: '#ffffff',
    headerTextColor: '#111827', striped: false, showBorders: true, hoverable: true,
  },
  'dark': {
    headerStyle: 'background', headerBackgroundColor: '#111827',
    headerTextColor: '#f9fafb', striped: true, showBorders: true, hoverable: true,
  },
  'forest': {
    headerStyle: 'background', headerBackgroundColor: '#2D5A3D',
    headerTextColor: '#ffffff', striped: true, showBorders: true, hoverable: true,
  },
  'compact': {
    rowSpacing: 'tight', rowPadding: 'compact', striped: true, showBorders: true,
  },

  // New themes (10 additions)
  'ocean': {
    headerStyle: 'background', headerBackgroundColor: '#0077BE',
    headerTextColor: '#ffffff', bodyTextColor: '#1E3A5F',
    alternateRowColor: '#E6F3FA', striped: true, showBorders: true, hoverable: true,
  },
  'sunset': {
    headerStyle: 'background', headerBackgroundColor: '#DC2626',
    headerTextColor: '#ffffff', bodyTextColor: '#7F1D1D',
    alternateRowColor: '#FEF2F2', striped: true, showBorders: true, hoverable: true,
  },
  'blue': {
    headerStyle: 'background', headerBackgroundColor: '#1E40AF',
    headerTextColor: '#ffffff', bodyTextColor: '#1E3A5F',
    alternateRowColor: '#EFF6FF', striped: true, showBorders: true, hoverable: true,
  },
  'corporate': {
    headerStyle: 'background', headerBackgroundColor: '#374151',
    headerTextColor: '#F9FAFB', bodyTextColor: '#1F2937',
    alternateRowColor: '#F3F4F6', striped: true, showBorders: true, hoverable: true,
  },
  'pastel': {
    headerStyle: 'background', headerBackgroundColor: '#E9D5FF',
    headerTextColor: '#581C87', bodyTextColor: '#6B7280',
    alternateRowColor: '#FDF4FF', striped: true, showBorders: false, hoverable: true,
  },
  'vibrant': {
    headerStyle: 'background', headerBackgroundColor: '#7C3AED',
    headerTextColor: '#ffffff', bodyTextColor: '#4C1D95',
    alternateRowColor: '#F5F3FF', striped: true, showBorders: true, hoverable: true,
  },
  'earth': {
    headerStyle: 'background', headerBackgroundColor: '#92400E',
    headerTextColor: '#ffffff', bodyTextColor: '#78350F',
    alternateRowColor: '#FFFBEB', striped: true, showBorders: true, hoverable: true,
  },
  'midnight': {
    headerStyle: 'background', headerBackgroundColor: '#0F172A',
    headerTextColor: '#E2E8F0', bodyTextColor: '#CBD5E1',
    alternateRowColor: '#1E293B', borderColor: '#334155',
    striped: true, showBorders: true, hoverable: true,
  },
  'clean': {
    headerStyle: 'typography', showBorders: true, striped: false,
    hoverable: true, rowPadding: 'normal', borderColor: '#E5E7EB',
  },
  'agricultural': {
    headerStyle: 'background', headerBackgroundColor: '#2D5A3D',
    headerTextColor: '#ffffff', bodyTextColor: '#1A3D28',
    alternateRowColor: '#ECFDF5', striped: true, showBorders: true, hoverable: true,
  },
};

const TABLE_THEME_ALIASES: Record<string, string> = {
  // Domain aliases
  farm: 'agricultural', agriculture: 'agricultural',
  nature: 'forest', tree: 'forest', woods: 'forest', green: 'forest',
  water: 'ocean', sea: 'ocean', aqua: 'ocean', marine: 'ocean',
  business: 'corporate', gray: 'corporate', grey: 'corporate',
  warm: 'sunset', fire: 'sunset', red: 'sunset',
  fall: 'earth', autumn: 'earth', brown: 'earth',
  // Style aliases
  night: 'midnight', black: 'midnight',
  purple: 'vibrant', violet: 'vibrant',
  soft: 'pastel', pink: 'pastel', light: 'clean',
  simple: 'clean', plain: 'clean', basic: 'clean',
  default: 'professional', standard: 'professional',
  bright: 'blue', cool: 'blue', cold: 'blue',
};

export class TableCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Title patterns
    this.commandPatterns.set(
      /(?:set |change )?(?:table )?title (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { title: match[1] } })
    );
    this.commandPatterns.set(
      /(?:set |change )?(?:table )?title (?:to )?(.+)/i,
      (match) => ({ properties: { title: match[1].trim().replace(/^["']|["']$/g, '') } })
    );

    // Sorting patterns
    this.commandPatterns.set(
      /(?:enable|turn on|add) sorting/i,
      () => ({ properties: { enableSorting: true } })
    );
    this.commandPatterns.set(
      /(?:disable|turn off|remove) sorting/i,
      () => ({ properties: { enableSorting: false } })
    );

    // Filtering patterns
    this.commandPatterns.set(
      /(?:enable|turn on|add) filter(?:ing|s)?/i,
      () => ({ properties: { enableFiltering: true, enableGlobalFilter: true } })
    );
    this.commandPatterns.set(
      /(?:disable|turn off|remove) filter(?:ing|s)?/i,
      () => ({ properties: { enableFiltering: false, enableGlobalFilter: false } })
    );

    // Pagination patterns
    this.commandPatterns.set(
      /(?:enable|turn on|add) pagination/i,
      () => ({ properties: { enablePagination: true } })
    );
    this.commandPatterns.set(
      /(?:disable|turn off|remove) pagination/i,
      () => ({ properties: { enablePagination: false } })
    );
    this.commandPatterns.set(
      /(?:set |change )?page ?size (?:to )?(\d+)/i,
      (match) => ({ properties: { pageSize: parseInt(match[1], 10) } })
    );
    this.commandPatterns.set(
      /show (\d+) rows? (?:per page|at a time)/i,
      (match) => ({ properties: { pageSize: parseInt(match[1], 10), enablePagination: true } })
    );

    // Row selection patterns
    this.commandPatterns.set(
      /(?:enable|turn on|add) (?:row )?selection/i,
      () => ({ properties: { enableRowSelection: true } })
    );
    this.commandPatterns.set(
      /(?:disable|turn off|remove) (?:row )?selection/i,
      () => ({ properties: { enableRowSelection: false } })
    );

    // Column resizing patterns
    this.commandPatterns.set(
      /(?:enable|turn on|add) (?:column )?resiz(?:ing|e|able)/i,
      () => ({ properties: { enableColumnResizing: true } })
    );

    // Virtualization patterns
    this.commandPatterns.set(
      /(?:enable|turn on|add) virtuali[sz]ation/i,
      () => ({ properties: { enableVirtualization: true } })
    );

    // Striped rows patterns
    this.commandPatterns.set(
      /(?:enable|add|make) (?:the )?(?:table )?(?:rows )?striped/i,
      () => ({ properties: { striped: true } })
    );
    this.commandPatterns.set(
      /(?:disable|remove) (?:the )?(?:table )?(?:rows )?strip(?:ed|es|ing)/i,
      () => ({ properties: { striped: false } })
    );

    // Hover state patterns
    this.commandPatterns.set(
      /(?:enable|add) (?:row )?hover/i,
      () => ({ properties: { hoverable: true } })
    );
    this.commandPatterns.set(
      /(?:disable|remove) (?:row )?hover/i,
      () => ({ properties: { hoverable: false } })
    );

    // Max height patterns
    this.commandPatterns.set(
      /(?:set |change )?(?:max(?:imum)? )?height (?:to )?(\d+)(?:px)?/i,
      (match) => ({ properties: { maxHeight: parseInt(match[1], 10) } })
    );

    // Dashboard mode toggle
    this.commandPatterns.set(
      /(?:enable|use|switch to) dashboard (?:mode|view)/i,
      () => ({ properties: { dashboardMode: true, enableSorting: false, enableFiltering: false, enablePagination: false } })
    );
    this.commandPatterns.set(
      /(?:disable|exit) dashboard (?:mode|view)/i,
      () => ({ properties: { dashboardMode: false } })
    );

    // Compact mode
    this.commandPatterns.set(
      /(?:make|set) (?:it |table )?compact/i,
      () => ({ properties: { rowSpacing: 'tight', rowPadding: 'compact' } })
    );
    this.commandPatterns.set(
      /(?:make|set) (?:it |table )?spacious/i,
      () => ({ properties: { rowSpacing: 'loose', rowPadding: 'spacious' } })
    );

    // ============================================
    // VISUAL / STYLE PATTERNS
    // ============================================

    // Header background color
    this.commandPatterns.set(
      /(?:set |change )?header (?:background |bg )(?:color )?(?:to )?(.+)/i,
      (match) => ({ properties: { headerBackgroundColor: this.parseColorValue(match[1].trim()) } })
    );

    // Header text color
    this.commandPatterns.set(
      /(?:set |change )?header (?:text |font )?color (?:to )?(.+)/i,
      (match) => ({ properties: { headerTextColor: this.parseColorValue(match[1].trim()) } })
    );

    // Header style mode
    this.commandPatterns.set(
      /(?:set |use )?(?:a )?(?:plain|typography|minimal) header/i,
      () => ({ properties: { headerStyle: 'typography' } })
    );
    this.commandPatterns.set(
      /(?:set |use )?(?:a )?(?:filled|background|colored) header/i,
      () => ({ properties: { headerStyle: 'background' } })
    );

    // Dark header shortcut
    this.commandPatterns.set(
      /(?:make|use) (?:a )?dark header/i,
      () => ({ properties: { headerStyle: 'background', headerBackgroundColor: '#1F2937', headerTextColor: '#ffffff' } })
    );

    // Show/hide borders
    this.commandPatterns.set(
      /(?:show|add|enable) (?:cell |row )?borders/i,
      () => ({ properties: { showBorders: true } })
    );
    this.commandPatterns.set(
      /(?:hide|remove|disable) (?:cell |row )?borders/i,
      () => ({ properties: { showBorders: false } })
    );

    // Font size
    this.commandPatterns.set(
      /(?:set |change )?(?:font |text )?size (?:to )?(\d+)(?:px)?/i,
      (match) => ({ style: { fontSize: parseInt(match[1], 10) } })
    );
    this.commandPatterns.set(
      /(?:make|set) (?:text |font )?(?:smaller|small)/i,
      () => ({ style: { fontSize: 12 } })
    );
    this.commandPatterns.set(
      /(?:make|set) (?:text |font )?(?:larger|large|bigger|big)/i,
      () => ({ style: { fontSize: 16 } })
    );

    // Row spacing
    this.commandPatterns.set(
      /(?:set |change )?row spacing (?:to )?(tight|normal|loose)/i,
      (match) => ({ properties: { rowSpacing: match[1].toLowerCase() } })
    );

    // Row padding
    this.commandPatterns.set(
      /(?:set |change )?(?:row |cell )?padding (?:to )?(compact|normal|spacious)/i,
      (match) => ({ properties: { rowPadding: match[1].toLowerCase() } })
    );

    // Body text color
    this.commandPatterns.set(
      /(?:set |change )?(?:body |row |cell |data )?text\s*color\s*(?:to )?\s*(#[0-9a-fA-F]{3,8}|\w+)/i,
      (match) => {
        const color = this.parseColorValue(match[1].trim());
        return color ? { properties: { bodyTextColor: color } } : null;
      }
    );

    // Alternate/striped row color
    this.commandPatterns.set(
      /(?:set |change )?(?:alternate|alternating|striped|even|odd)\s*row\s*(?:background |bg )?color\s*(?:to )?\s*(#[0-9a-fA-F]{3,8}|\w+)/i,
      (match) => {
        const color = this.parseColorValue(match[1].trim());
        return color ? { properties: { alternateRowColor: color, striped: true } } : null;
      }
    );

    // Row hover color
    this.commandPatterns.set(
      /(?:set |change )?(?:row )?hover\s*(?:background |bg )?color\s*(?:to )?\s*(#[0-9a-fA-F]{3,8}|\w+)/i,
      (match) => {
        const color = this.parseColorValue(match[1].trim());
        return color ? { properties: { rowHoverColor: color, hoverable: true } } : null;
      }
    );

    // Border color
    this.commandPatterns.set(
      /(?:set |change )?border\s*color\s*(?:to )?\s*(#[0-9a-fA-F]{3,8}|\w+)/i,
      (match) => {
        const color = this.parseColorValue(match[1].trim());
        return color ? { properties: { borderColor: color, showBorders: true } } : null;
      }
    );

    // Compound: "make it dark" / "make it light"
    this.commandPatterns.set(
      /make\s*(?:it |table )?(?:look )?dark(?:er)?/i,
      () => ({ properties: TABLE_THEMES['midnight'] })
    );

    this.commandPatterns.set(
      /make\s*(?:it |table )?(?:look )?light(?:er)?/i,
      () => ({ properties: TABLE_THEMES['clean'] })
    );

    // Table themes
    this.commandPatterns.set(
      /(?:use |apply |set )?(?:the )?(\w[\w-]*)\s*(?:table )?theme/i,
      (match) => {
        const themeName = match[1].toLowerCase();
        const resolved = TABLE_THEME_ALIASES[themeName] || themeName;
        if (TABLE_THEMES[resolved]) {
          return { properties: TABLE_THEMES[resolved] };
        }
        return null;
      }
    );

    // Responsive breakpoint prefix
    this.commandPatterns.set(
      /^on\s+(mobile|tablet|desktop)\s+(.+)$/i,
      (match, component) => this.handleBreakpointCommand(match[1], match[2], component)
    );
  }

  private handleBreakpointCommand(
    breakpoint: string,
    subCommand: string,
    component: Component
  ): PropertyChange | null {
    const result = this.matchPatterns(subCommand, component);
    if (!result) return null;

    const bp = breakpoint.toLowerCase();
    return {
      properties: {
        [`responsive_${bp}`]: result.properties,
      },
    };
  }

  override canHandle(command: string, component: Component): boolean {
    const isTable = component.type === ComponentType.TABLE || component.type === ComponentType.ADVANCED_TABLE;
    if (!isTable) return false;

    const tableKeywords = [
      'sort', 'filter', 'pagination', 'page', 'row', 'column',
      'select', 'resize', 'virtual', 'stripe', 'hover', 'height',
      'compact', 'spacious', 'dashboard', 'title',
      'header', 'border', 'theme', 'dark', 'minimal', 'professional',
      'font', 'size', 'color', 'spacing', 'padding',
      // Phase 3 keywords
      'body', 'alternate', 'light', 'make',
      'ocean', 'midnight', 'corporate', 'earth', 'vibrant', 'pastel',
      'sunset', 'blue', 'clean', 'agricultural',
    ];
    return tableKeywords.some(kw => command.toLowerCase().includes(kw));
  }

  protected override calculateConfidence(command: string, changes: PropertyChange): number {
    let confidence = super.calculateConfidence(command, changes);

    // Boost confidence for commands that closely match table patterns
    const exactPatterns = [
      /^(?:enable|disable|turn on|turn off|add|remove) \w+$/i,
      /^(?:set|change) .+ to .+$/i,
      /^(?:make|use) .+$/i,
      /^show \d+ rows/i,
    ];
    if (exactPatterns.some(p => p.test(command.trim()))) {
      confidence = Math.max(confidence, 0.9);
    }

    return Math.min(confidence, 1.0);
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const changes = this.matchPatterns(command, component);
    if (changes) {
      return {
        success: true,
        command: {
          type: this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description: this.generateDescription(changes, component),
        },
      };
    }

    return {
      success: false,
      error: 'Could not parse table command',
      suggestions: await this.getSuggestions(component),
    };
  }

  async getExampleCommands(componentType: ComponentType): Promise<string[]> {
    if (componentType === ComponentType.ADVANCED_TABLE) {
      return [
        'enable row selection',
        'enable column resizing',
        'use professional theme',
        'use ocean theme',
        'make dark header',
        'make it compact',
        'set body text color to #333333',
      ];
    }
    return [
      'enable sorting',
      'enable filtering',
      'use professional theme',
      'use ocean theme',
      'make dark header',
      'add borders',
      'make it striped',
      'make it dark',
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const props = component.properties || {};
    const suggestions: string[] = [];

    if (props.enableSorting === false) suggestions.push('enable sorting');
    if (props.enableFiltering === false) suggestions.push('enable filtering');
    if (!props.enablePagination) suggestions.push('enable pagination');
    if (!props.enableRowSelection) suggestions.push('enable row selection');
    if (!props.striped) suggestions.push('make it striped');
    if (!props.showBorders) suggestions.push('add borders');
    if (props.headerStyle !== 'background') suggestions.push('use dark header');
    if (!props.hoverable) suggestions.push('enable row hover');
    if (!props.bodyTextColor) suggestions.push('set body text color to #333333');
    if (!props.alternateRowColor && props.striped) suggestions.push('set alternate row color to #F0F8FF');
    if (!props.headerBackgroundColor) suggestions.push('use ocean theme');
    if (props.headerStyle !== 'background' || !props.headerBackgroundColor) suggestions.push('make it dark');

    return suggestions.slice(0, 5);
  }
}

export const tableCommandHandler = new TableCommandHandler();
